import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Icon from '../Shared/Icons';
import { getProgram } from '../../config/programs';
import { fetchMembers, fetchReports } from '../../utils/programData';
import { analyseProgram, analyseSessions } from '../../utils/programAI';
import { isoWeekKey, weekLabel } from '../../utils/weekUtils';
import { ProgramShell, S, T } from './ui';

export default function ProgramAI() {
  const { pid } = useParams();
  const program = getProgram(pid);

  const [data, setData] = useState(null);
  const [out, setOut] = useState('');
  const [mode, setMode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (program) load(); /* eslint-disable-next-line */ }, [pid]);

  async function load() {
    try {
      const [members, individual, sessions, staff] = await Promise.all([
        fetchMembers(pid),
        fetchReports({ programId: pid, kind: 'individual' }),
        fetchReports({ programId: pid, kind: 'session' }),
        fetchReports({ kind: 'staffWeekly' }),
      ]);
      setData({ members, individual, sessions, staff });
    } catch (e) { console.error(e); }
  }

  async function run(kind) {
    if (!data) return;
    setBusy(true); setErr(''); setOut(''); setMode(kind);
    try {
      if (kind === 'program') {
        setOut(await analyseProgram({
          programId: pid,
          members: data.members,
          individual: data.individual,
          sessions: data.sessions,
          staffWeekly: data.staff,
        }));
      } else if (kind === 'week') {
        const wk = isoWeekKey();
        setOut(await analyseProgram({
          programId: pid,
          members: data.members,
          individual: data.individual.filter(r => r.weekKey === wk),
          sessions: data.sessions.filter(r => r.weekKey === wk),
          staffWeekly: data.staff.filter(r => r.weekKey === wk),
        }));
      } else {
        setOut(await analyseSessions(data.sessions, pid));
      }
    } catch (e) {
      console.error(e);
      setErr('AI haipatikani kwa sasa. Angalia mtandao kisha jaribu tena.');
    }
    setBusy(false);
  }

  if (!program) return <Navigate to="/programs" />;

  const options = [
    { k: 'program', label: 'Programu nzima', sub: 'Mwenendo wa jumla tangu mwanzo', icon: 'chart', color: program.color },
    { k: 'week', label: 'Wiki hii tu', sub: weekLabel(), icon: 'clock', color: '#4527A0' },
    { k: 'sessions', label: 'Session na mafunzo', sub: 'Nini kiboreshwe kwenye session', icon: 'calendar', color: '#00695C' },
  ];

  return (
    <ProgramShell program={program} tab="ai">

      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '9px' }}>
          <Icon name="sparkle" size={19} color={T.orange} />
          <h2 style={{ ...S.h2, marginBottom: 0 }}>Uchambuzi wa AI</h2>
        </div>
        <div style={{ ...S.sub }}>
          AI inasoma ripoti halisi zilizoandikwa na staff — si makisio.
          Inakusaidia kujua nini kinafanya kazi na nini kiboreshwe kwenye mafunzo na activity.
        </div>

        {data && (
          <div style={{ display: 'flex', gap: '9px', marginTop: '15px', flexWrap: 'wrap' }}>
            <Chip value={data.members.length} label="Wanachama" color={program.color} />
            <Chip value={data.individual.length} label="Ripoti binafsi" color="#059669" />
            <Chip value={data.sessions.length} label="Ripoti za session" color="#4527A0" />
          </div>
        )}
      </div>

      {options.map(o => (
        <div key={o.k} onClick={() => !busy && run(o.k)} style={{
          ...S.card, cursor: busy ? 'default' : 'pointer', padding: '15px 18px',
          display: 'flex', alignItems: 'center', gap: '12px',
          opacity: busy && mode !== o.k ? 0.5 : 1,
          borderLeft: mode === o.k ? `4px solid ${o.color}` : `1px solid ${T.line}`,
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '11px', flexShrink: 0,
            background: `${o.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name={o.icon} size={16} color={o.color} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: T.ink }}>{o.label}</div>
            <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '2px' }}>{o.sub}</div>
          </div>
          <span style={{ fontSize: '11.5px', fontWeight: 700, color: o.color }}>
            {busy && mode === o.k ? 'Inafikiri...' : 'Chambua'}
          </span>
        </div>
      ))}

      {err && <div style={S.err}>{err}</div>}

      {out && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ ...S.h2, marginBottom: 0 }}>Matokeo</h2>
            <button style={{ ...S.btnGhost, padding: '7px 13px', fontSize: '11.5px' }}
              onClick={() => window.print()}>Chapisha</button>
          </div>
          <div style={{
            fontSize: '13.5px', color: T.ink, lineHeight: 1.85, whiteSpace: 'pre-wrap',
            background: '#FAF8F3', padding: '16px', borderRadius: '12px',
          }}>{out}</div>
          <div style={{ fontSize: '11px', color: T.muted, marginTop: '11px', lineHeight: 1.6 }}>
            <Icon name="info" size={11} /> Huu ni msaada wa kufikiri, si uamuzi wa mwisho.
            Hakiki na uzingatie unachokijua kutoka field.
          </div>
        </div>
      )}

    </ProgramShell>
  );
}

function Chip({ value, label, color }) {
  return (
    <div style={{ background: `${color}0F`, borderRadius: '10px', padding: '8px 13px' }}>
      <div style={{ fontSize: '17px', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '10.5px', color: T.muted, marginTop: '3px' }}>{label}</div>
    </div>
  );
}
